import React from 'react';
import { unitConversionService as unitService } from '@/services/unit-conversion.service';
import { WeaveInput } from '@/components/UI/WeaveInput/WeaveInput';
import { Icon } from '@/components/UI/Icon/Icon';
import MetricBox from './MetricBox';
import styles from './styles.module.scss';

interface SpaceCardProps {
	title: string;
	grossArea: number;
	tare: number;
	used: number;
	isOpen: boolean;
	children?: React.ReactNode;
	onTareChange: (tare: number) => void;
	onToggle: () => void;
}

const SpaceCard = ({
	title,
	grossArea,
	tare,
	used,
	isOpen,
	onTareChange,
	onToggle,
	children,
}: SpaceCardProps) => {
	const activeHeaderClass = isOpen ? styles.cardHeaderActive : '';

	const unit = unitService.getUnitAbbreviation('area');
	const netArea = grossArea - grossArea * tare;
	const tareArea = Math.round(grossArea * tare);
	const leftArea = netArea - used;
	const usedPercentage = (used / netArea) * 100;

	const grossDisplay = unitService.formatWithPreferredUnit(grossArea, 'area', 'm2');
	const tareDisplay = unitService.formatWithPreferredUnit(tareArea, 'area', 'm2');
	const netDisplay = unitService.formatWithPreferredUnit(netArea, 'area', 'm2');
	const usedDisplay = unitService.formatWithPreferredUnit(used, 'area', 'm2');
	const leftDisplay = unitService.formatWithPreferredUnit(leftArea, 'area', 'm2');

	const statusClass =
		usedPercentage > 100 || usedPercentage < 0 || !isFinite(usedPercentage)
			? styles.statusDanger
			: styles.statusSuccess;
	const activeCardClass = isOpen ? styles.spaceCardActive : '';

	return (
		<div className={`${styles.categoryCard} ${activeCardClass}`}>
			<div className={styles.cardPadding}>
				<div className={`${styles.cardHeader} ${activeHeaderClass}`} onClick={onToggle}>
					<div className={styles.titleWrapper}>
						<Icon
							className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
							name="forma-icon-arrow-right"
						/>
						<h3 className={styles.spaceCardTitle}>{title}</h3>
					</div>

					<div className={styles.tareContainer} onClick={(e) => e.stopPropagation()}>
						<span>Tare:</span>
						<WeaveInput
							className={styles.weaveInput}
							style={{ width: '60px' }}
							type="number"
							value={tare}
							step="0.01"
							unit="%"
							max={1}
							onChange={(e) => {
								const value = parseFloat((e.target as HTMLInputElement).value);
								onTareChange(value);
							}}
						/>
						<small>({tareDisplay})</small>
					</div>
				</div>

				<div className={styles.metricsGrid}>
					<MetricBox label="Gross Area" value={grossDisplay} />
					<MetricBox label="Net Usable Area" value={netDisplay} highlighted />
					<MetricBox label="Used Area" value={usedDisplay} />
					<div className={styles.metricEnd}>
						<span className={styles.metricBoxLabel}>Remaining Area</span>
						<span className={`${styles.bigValue} ${statusClass}`}>{leftDisplay}</span>
					</div>
				</div>

				<div className={styles.usageBarBg}>
					<div
						className={`${styles.usageBarFill} ${statusClass}`}
						style={{ width: `${Math.min(usedPercentage, 100)}%` }}
					></div>
				</div>
				<div className={styles.usageLabels}>
					<span>0 {unit}</span>
					<span>Max: {netDisplay}</span>
				</div>

				{isOpen && <div className={styles.cardBody}>{children}</div>}
			</div>
		</div>
	);
};

export default SpaceCard;
